import axios from 'axios';

import runtimeConfig from '@/common/runtime-config';
import { noAccessAlert } from '@/common/utils/noAccessAlert';

import router from '@/router';
import store from '@/store';

export function configAxios() {
  axios.defaults.baseURL = runtimeConfig.apiBaseUrl;
  axios.defaults.maxContentLength = Infinity;
  axios.defaults.maxBodyLength = Infinity;
}

const authAxios = axios.create({
  baseURL: runtimeConfig.apiAuthUrl,
  maxContentLength: Infinity,
  maxBodyLength: Infinity,
});

function attachToken(config) {
  const token = store.getters['auth/oidc/getAccessToken'];
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

axios.interceptors.request.use((config) => {
  config.params = { ...config.params };
  return attachToken(config);
});

authAxios.interceptors.request.use((config) => attachToken(config));

// Response: 401 → refresh → retry (с очередью параллельных запросов)
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error) => {
  failedQueue.forEach(({ resolve, reject }) => {
    if (error) reject(error);
    else resolve();
  });
  failedQueue = [];
};

function retryWithToken(request) {
  const token = store.getters['auth/oidc/getAccessToken'];
  request.headers.Authorization = `Bearer ${token}`;
  const instance = request.baseURL === runtimeConfig.apiAuthUrl ? authAxios : axios;
  return instance(request);
}

async function handleResponseError(error) {
  const originalRequest = error.config;

  if (error?.response?.status === 401) {
    if (!store.getters['auth/oidc/isAuthenticated']) {
      throw error;
    }

    if (originalRequest._retry) throw error;
    originalRequest._retry = true;

    if (isRefreshing)
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => retryWithToken(originalRequest));

    isRefreshing = true;

    try {
      await store.dispatch('auth/oidc/refreshToken');
      processQueue(null);
      return retryWithToken(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError);
      store.commit('auth/oidc/clearAuth');
      router.push({ name: 'Login' });
      throw refreshError;
    } finally {
      isRefreshing = false;
    }
  }

  if (error?.response?.status === 403 && !originalRequest?.skipForbiddenRedirect) {
    router.push('/');
    noAccessAlert();
  }

  return Promise.reject(error);
}

axios.interceptors.response.use((response) => response, handleResponseError);
authAxios.interceptors.response.use((response) => response, handleResponseError);

export { authAxios };
